import React from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, BarChart3 } from 'lucide-react';
import { StatCard, DataTable } from './UIComponents';
import { formatPrice, formatNumber, formatVolume } from './formatters';

const StockOverview = ({ data, openCloseData }) => {
  const { t } = useTranslation();
  // Key metrics cards
  const keyMetrics = [
    {
      label: t('stocks.fields.previousClose'),
      value: formatPrice(data.previousClose),
      tooltip: t('stocks.tooltips.previousClose'),
      trend: data.change
    },
    {
      label: t('stocks.fields.marketCap'),
      value: formatNumber(data.marketCap),
      tooltip: t('stocks.tooltips.marketCap')
    },
    {
      label: t('stocks.fields.volume'),
      value: formatVolume(data.volume),
      tooltip: t('stocks.tooltips.volume')
    },
    {
      label: t('stocks.fields.week52High'),
      value: formatPrice(data.weekHigh52),
      tooltip: t('stocks.tooltips.week52High')
    }
  ];

  // Price data table
  const priceData = [
    { label: t('stocks.fields.previousClose'), value: formatPrice(data.previousClose) },
    { label: t('stocks.fields.open'), value: formatPrice(data?.open) },
    { label: t('stocks.fields.daysRange'), value: `${formatPrice(data?.low)} - ${formatPrice(data?.high)}` },
    { label: t('stocks.fields.week52Range'), value: `${formatPrice(data.weekLow52)} - ${formatPrice(data.weekHigh52)}` },
    { label: t('stocks.fields.preMarket'), value: formatPrice(openCloseData?.preMarket) },
    { label: t('stocks.fields.bestPrice'), value: formatPrice(openCloseData?.best_price) },
  ];

  // Market data table
  const marketData = [
    { label: t('stocks.fields.marketCap'), value: formatNumber(data.marketCap, '$') },
    { label: t('stocks.fields.approxEnterpriseValue'), value: formatNumber(data.marketCap && data.liabilities && data.currentAssets ? data.marketCap + data.liabilities - data.currentAssets : null, '$') },
    { label: t('stocks.fields.sharesOutstanding'), value: formatNumber(data.sharesOutstanding) },
    { label: t('stocks.fields.float'), value: formatNumber(data.sharesOutstanding) },
    { label: t('stocks.fields.volume'), value: formatVolume(data.volume) },
    { label: t('stocks.fields.avgVolume10d'), value: formatVolume(data.avgVolume10) },
    { label: t('stocks.fields.avgVolume30d'), value: formatVolume(data.avgVolume30) },
  ];

  return (
    <div className="space-y-8">
      {/* Key metrics cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {keyMetrics.map((metric, index) => (
          <StatCard
            key={index}
            label={metric.label}
            value={metric.value}
            tooltip={metric.tooltip}
            trend={metric.trend}
          />
        ))}
      </div>

      {/* Data tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DataTable
          title={t('stocks.tableTitles.priceData')}
          icon={DollarSign}
          data={priceData}
          defaultExpanded={true}
        />
        <DataTable
          title={t('stocks.tableTitles.marketData')}
          icon={BarChart3}
          data={marketData}
          defaultExpanded={true}
        />
      </div>
    </div>
  );
};

export default StockOverview;